import { ClassValue } from 'clsx';
import { View } from '@shared/components/View';
import { RelatedLookupField } from '@shared/data/entity/fields/RelatedLookupField';
import { LazyRelatedLookupOverflowMenu } from '@shared/scenes/entity/components/fields/RelatedCardLookupEditField/LazyRelatedLookupOverflowMenu';
import { getComponentLabel, isRelatedCardLookupComponent } from '@shared/scenes/workflows/utils';
import { API } from '@shared/types/api';
import { AddressCollectionViewField } from './AddressCollectionViewField';
import { CollectionViewFieldContainer } from './CollectionViewFieldContainer';
import { DateCollectionViewField } from './DateCollectionViewField';
import { EmailCollectionViewField } from './EmailCollectionViewField';
import { FieldVariant } from './FieldVariant';
import { NumberCollectionViewField } from './NumberCollectionViewField';
import { TagCollectionViewField } from './TagCollectionViewField';
import { TextCollectionViewField } from './TextCollectionViewField';
import styles from './RelatedLookupCollectionViewField.module.css';

interface Props {
  variant: FieldVariant;
  className?: ClassValue;
  field: RelatedLookupField;
  component: API.AnyComponent;
  workflowTemplateId: string;
}

/**
 * The first related card's looked-up value, in this variant of its own field type. Compact and
 * inline are that field alone, laid out by the host as it would lay out the field itself, so a
 * host's rules for a type (a slot truncating text) apply to a looked-up value of it too. The
 * default adds a menu of the other cards' values.
 */
export function RelatedLookupCollectionViewField({
  field,
  component,
  workflowTemplateId,
  variant,
  className,
}: Props) {
  if (field.isEmpty) {
    return null;
  }

  if (variant !== 'default') {
    return <LeafField field={field} variant={variant} className={className} />;
  }

  const extraCount = field.count - 1;
  const showOverflow = extraCount > 0 && isRelatedCardLookupComponent(component);

  return (
    <CollectionViewFieldContainer
      fieldType={field.fieldType}
      variant={variant}
      className={[styles.relatedLookupCollectionViewField, className]}
    >
      <LeafField field={field} variant={variant} />
      {showOverflow && (
        <View stopPropagation>
          <LazyRelatedLookupOverflowMenu
            extraCount={extraCount}
            component={component}
            relatedField={field.relatedField}
            workflowTemplateId={workflowTemplateId}
            title={`${getComponentLabel(component)} (${field.count})`}
          />
        </View>
      )}
    </CollectionViewFieldContainer>
  );
}

function LeafField({
  field,
  variant,
  className,
}: {
  field: RelatedLookupField;
  variant: FieldVariant;
  className?: ClassValue;
}) {
  const leafField = field.firstLeafField;
  if (leafField === null) {
    return null;
  }

  switch (leafField.fieldType) {
    case 'AddressField':
      return (
        <AddressCollectionViewField field={leafField} variant={variant} className={className} />
      );
    case 'DateField':
      return <DateCollectionViewField field={leafField} variant={variant} className={className} />;
    case 'EmailField':
      return <EmailCollectionViewField field={leafField} variant={variant} className={className} />;
    case 'NumberField':
      return (
        <NumberCollectionViewField field={leafField} variant={variant} className={className} />
      );
    case 'TagField':
      return <TagCollectionViewField field={leafField} variant={variant} className={className} />;
    case 'TextField':
      return <TextCollectionViewField field={leafField} variant={variant} className={className} />;
    default:
      return null;
  }
}
